/**
 * LoginForm Component
 *
 * Complete functional login form component with comprehensive validation,
 * error handling, and seamless authentication integration.
 */

// 1. External dependencies
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

// 2. Internal absolute imports
import { Button, Input, Checkbox, Form, Alert, Link, Card, CardBody, CardFooter } from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/services/UserService';
import type { AuthErrorCode } from '@/types/auth';

/**
 * Login Form Component
 *
 * Features:
 * - HeroUI Form and Input components with native validation
 * - Real-time email and password validation
 * - Server-side error mapping and display
 * - Remember me functionality
 * - Return URL preservation after login
 * - Mobile responsive and accessible design
 */
export function LoginForm(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  // Autofill detection and re-render management
  const [renderKey, setRenderKey] = useState(0);
  const autofillTimeoutRef = useRef<number | null>(null);
  const lastValuesRef = useRef({ email: '', password: '' });

  // Debounced autofill completion detection
  const triggerAutofillFix = useCallback(() => {
    // Clear any existing timeout
    if (autofillTimeoutRef.current) {
      window.clearTimeout(autofillTimeoutRef.current);
    }

    // Set a new timeout to trigger re-render after autofill is likely complete
    autofillTimeoutRef.current = window.setTimeout(() => {
      setRenderKey(prev => prev + 1);
    }, 150); // 150ms delay to allow autofill to complete multiple fields
  }, []);

  // Initialize lastValuesRef with current form values
  useEffect(() => {
    lastValuesRef.current = {
      email: formData.email,
      password: formData.password
    };
  }, [formData.email, formData.password]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autofillTimeoutRef.current) {
        window.clearTimeout(autofillTimeoutRef.current);
      }
    };
  }, []);

  // Helper to determine default redirect based on user role
  const getDefaultRedirect = useCallback((userRole?: string): string => {
    if (userRole === 'admin') {
      return '/admin/dashboard';
    }
    return '/dashboard';
  }, []);

  // Get intended destination from location state
  const from = (location.state as { from?: string })?.from;

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible(prev => !prev);
  }, []);

  // Email validation function
  const validateEmail = (value: string): string | null => {
    if (!value) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return null;
  };

  // Validation state - show email errors after blur or submit
  const emailError = useMemo(() => {
    if (!hasSubmitted && !emailTouched) return null;
    return validateEmail(formData.email);
  }, [formData.email, hasSubmitted, emailTouched]);

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasSubmitted(true); // Mark that user has attempted to submit
    setIsSubmitting(true);
    setServerErrors({});

    try {
      const result = await login({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe
      });

      if (result.success) {
        // Check if user needs onboarding by fetching complete user data
        try {
          const userData = await userService.get(result.user.id);
          if (userData && !userData.onboardingCompleted) {
            // User hasn't completed onboarding - redirect to onboarding
            console.log('🎯 LoginForm: User needs onboarding, redirecting to /onboarding');
            navigate('/onboarding', { replace: true });
          } else {
            // Determine destination: use 'from' if provided, otherwise use role-based default
            const destination = from || getDefaultRedirect(result.user.role);
            console.log(`🎯 LoginForm: Redirecting ${result.user.role || 'user'} to ${destination}`);
            navigate(destination, { replace: true });
          }
        } catch (error) {
          console.error('❌ LoginForm: Error checking onboarding status:', error);
          // If we can't check onboarding status, use role-based default
          const destination = from || getDefaultRedirect(result.user.role);
          navigate(destination, { replace: true });
        }
      } else {
        // Map authentication errors to form field errors
        const errors: Record<string, string> = {};

        switch (result.error as AuthErrorCode) {
          case 'INVALID_CREDENTIALS':
            errors['email'] = 'Invalid email or password';
            break;
          case 'ACCOUNT_NOT_FOUND':
            errors['email'] = 'No account found with this email address';
            break;
          case 'ACCOUNT_DISABLED':
            errors['email'] = 'This account has been disabled';
            break;
          case 'EMAIL_NOT_VERIFIED':
            errors['email'] = 'Please verify your email address before signing in';
            break;
          case 'RATE_LIMITED':
            errors['general'] = 'Too many login attempts. Please try again later';
            break;
          default:
            errors['general'] = result.message || 'Login failed. Please try again';
        }

        setServerErrors(errors);
      }
    } catch (error) {
      console.error('Login error:', error);
      setServerErrors({ 'general': 'Network error occurred. Please check your connection' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout hideFooter={true}>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-md mx-auto p-6 mt-10">
          <Form
            validationBehavior="aria"
            validationErrors={serverErrors}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Email Input */}
            <Input
              type="email"
              label="Email"
              placeholder="Enter your email"
              value={formData.email}
              onValueChange={(value) => {
                const oldEmail = lastValuesRef.current.email;
                lastValuesRef.current.email = value;

                // Detect if this might be autofill (rapid value change from empty to filled)
                if (oldEmail === '' && value !== '' && value.includes('@')) {
                  triggerAutofillFix();
                }

                setFormData(prev => ({ ...prev, email: value }));
              }}
              onBlur={() => setEmailTouched(true)}
              validate={validateEmail}
              isRequired
              isClearable
              autoComplete="email"
              errorMessage={serverErrors['email'] || emailError}
              isInvalid={!!serverErrors['email'] || !!emailError}
              classNames={{
                input: "text-base", // 16px font size to prevent mobile zoom
              }}
            />

            {/* Password Input */}
            <Input
              key={`password-${renderKey}`}
              type={isPasswordVisible ? "text" : "password"}
              label="Password"
              placeholder="Enter your password"
              value={formData.password}
              onValueChange={(value) => {
                const oldPassword = lastValuesRef.current.password;
                lastValuesRef.current.password = value;

                // Detect if this might be autofill (rapid value change from empty to filled)
                if (oldPassword === '' && value !== '' && value.length > 3) {
                  triggerAutofillFix();
                }

                setFormData(prev => ({ ...prev, password: value }));
              }}
              isRequired
              autoComplete="current-password"
              errorMessage={serverErrors['password']}
              isInvalid={!!serverErrors['password']}
              endContent={
                <button
                  className="focus:outline-none"
                  type="button"
                  onClick={togglePasswordVisibility}
                  aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                >
                  {isPasswordVisible ? (
                    <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                  )}
                </button>
              }
              classNames={{
                input: "text-base", // 16px font size to prevent mobile zoom
              }}
            />

            {/* Remember Me Checkbox */}
            <Checkbox
              isSelected={formData.rememberMe}
              onValueChange={(checked) => setFormData(prev => ({ ...prev, rememberMe: checked }))}
              // className="mt-10"
            >
              Remember me
            </Checkbox>

            {/* General Error Display */}
            {serverErrors['general'] && (
              <Alert
                color="warning"
                variant="flat"
                description={serverErrors['general']}
              />
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              color="primary"
              size="lg"
              isLoading={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? 'Signing In...' : 'Sign In'}
            </Button>
          </Form>
        </CardBody>
        {/* Auth Navigation Links - Positioned at bottom */}
        <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              Not a beta tester?{' '}
              <Link as={RouterLink} {...({ to: "/beta-application" } as { to: string })} color="primary" size="sm">
                Apply to be one here
              </Link>
            </p>
            <p className="text-sm">
              <Link as={RouterLink} {...({ to: "/password-reset" } as { to: string })} color="foreground" size="sm">
                Forgot your password?
              </Link>
            </p>
          </div>
        </CardFooter>
      </Card>
    </AppLayout>
  );
}